import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './ThnksSelectionPage.css';

/**
 * Component responsible for rendering the Thnks selection page, which allows users to select gifts, templates, and assign them to recipients. Handles gift-specific logic, validation, and state management.
 *
 * @param {Object} props The props for the ThnksSelectionPage component.
 * @param {string} props.jobId The unique identifier for the job tied to this session.
 * @param {Object} props.validationData Data structure containing recipient validation results and original input details.
 * @param {Function} props.setValidationData Function to update the validationData state object when necessary.
 * @return {JSX.Element} The rendered JSX for the Thnks selection page.
 */
function ThnksSelectionPage({ jobId, validationData, setValidationData }) {
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Gift behavior IDs from API (school selection required / auto-select all recipients)
  const [requiresSchoolSelectionGiftIds, setRequiresSchoolSelectionGiftIds] = useState([]);
  const [autoSelectAllGiftIds, setAutoSelectAllGiftIds] = useState([]);

  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [customSchool, setCustomSchool] = useState('');
  const [showCustomSchoolInput, setShowCustomSchoolInput] = useState(false);

  // Per-recipient assignments: { row_number: { giftId, templateId, message } }
  const [recipientAssignments, setRecipientAssignments] = useState({});

  // Currently selected recipients for bulk assignment
  const [selectedForAssignment, setSelectedForAssignment] = useState([]);

  // Hover preview state
  const [hoveredRecipient, setHoveredRecipient] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef(null);

  // Sender info cache: { email: { first_name, last_name, full_name } }
  const [senderInfoCache, setSenderInfoCache] = useState({});

  // Shipping details modal state
  const [showShippingModal, setShowShippingModal] = useState(false);

  // Message character limit to fit on one page
  const MESSAGE_CHAR_LIMIT = 855;

  // Get valid recipients from validation data
  const validRecipients = validationData?.results?.filter(
    r => r.status === 'valid' || r.status === 'corrected'
  ).map(r => ({
    row_number: r.row_number,
    first_name: r.original_data?.first_name || r.validated_data?.first_name || '',
    last_name: r.original_data?.last_name || r.validated_data?.last_name || '',
    email: r.original_data?.sender_email || r.validated_data?.sender_email || '',
    city: r.validated_data?.city || r.original_data?.city || '',
    state: r.validated_data?.state || r.original_data?.state || '',
    address_1: r.validated_data?.address_1 || r.original_data?.address_1 || '',
    zip_code: r.validated_data?.zip_code || r.original_data?.zip_code || '',
  })) || [];

  useEffect(() => {
    if (!jobId) {
      navigate('/');
      return;
    }

    const loadData = async () => {
      try {
        const [giftsResponse, schoolsResponse, behaviorsResponse] = await Promise.all([
          apiService.getAvailableGifts(),
          fetch('/ncaa-schools.json').then(res => res.json()).catch(() => []),
          apiService.getGiftBehaviors(),
        ]);

        setGifts(giftsResponse.data.gifts || []);
        setRequiresSchoolSelectionGiftIds(Array.isArray(behaviorsResponse.data.requires_school_selection_gift_ids) ? behaviorsResponse.data.requires_school_selection_gift_ids : []);
        setAutoSelectAllGiftIds(Array.isArray(behaviorsResponse.data.auto_select_all_gift_ids) ? behaviorsResponse.data.auto_select_all_gift_ids : []);
        // Templates are loaded when a gift is selected
        setTemplates([]);
        setSchools(schoolsResponse || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load gifts');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [jobId, navigate]);

  // Load sender info for all unique sender emails
  useEffect(() => {
    const loadSenderInfo = async () => {
      // Get unique sender emails
      const uniqueEmails = [...new Set(validRecipients.map(r => r.email).filter(Boolean))];

      // Look up each email that's not already in cache
      for (const email of uniqueEmails) {
        if (!senderInfoCache[email]) {
          try {
            const response = await apiService.lookupUser(email);
            if (response.data.found) {
              setSenderInfoCache(prev => ({
                ...prev,
                [email]: {
                  first_name: response.data.first_name,
                  last_name: response.data.last_name,
                  full_name: response.data.full_name
                }
              }));
            }
          } catch (err) {
            console.error(`Error looking up sender ${email}:`, err);
          }
        }
      }
    };

    if (validRecipients.length > 0) {
      loadSenderInfo();
    }
  }, [validRecipients.length]);

  // Load templates when gift is selected
  useEffect(() => {
    if (!selectedGift) {
      setTemplates([]);
      setSelectedTemplate(null);
      setCustomMessage('');
      return;
    }

    const loadTemplates = async () => {
      try {
        const response = await apiService.getMessageTemplates(selectedGift);
        setTemplates(response.data.templates || []);
        // Clear previously selected template when gift changes
        setSelectedTemplate(null);
        setCustomMessage('');
      } catch (err) {
        console.error('Error loading templates:', err);
        setError('Failed to load message templates');
      }
    };

    loadTemplates();
  }, [selectedGift]);

  // Update custom message when template changes
  useEffect(() => {
    if (selectedTemplate) {
      const template = templates.find(t => t.id === selectedTemplate);
      if (template) {
        setCustomMessage(template.message);
      }
    }
  }, [selectedTemplate, templates]);

  // Reset school selection when gift changes
  useEffect(() => {
    if (!requiresSchoolSelectionGiftIds.includes(selectedGift)) {
      setSelectedSchool('');
      setSchoolSearch('');
      setCustomSchool('');
      setShowCustomSchoolInput(false);
    }
  }, [selectedGift, requiresSchoolSelectionGiftIds]);

  // Auto-select all unassigned recipients when gift with auto-select behavior is selected
  useEffect(() => {
    if (autoSelectAllGiftIds.includes(selectedGift)) {
      const unassignedRecipients = validRecipients.filter(
        r => !recipientAssignments[r.row_number]
      );
      setSelectedForAssignment(unassignedRecipients.map(r => r.row_number));
    }
  }, [selectedGift, autoSelectAllGiftIds]);

  // Check if school selection is required and valid
  const needsSchoolSelection = requiresSchoolSelectionGiftIds.includes(selectedGift);
  const hasValidSchool = selectedSchool || customSchool;

  // Filter schools based on search
  const filteredSchools = schoolSearch.trim()
    ? schools.filter(school =>
        school.toLowerCase().includes(schoolSearch.toLowerCase())
      )
    : schools;

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    setSchoolSearch('');
    setShowCustomSchoolInput(false);
    setCustomSchool('');
  };

  const handleCustomSchoolToggle = () => {
    setShowCustomSchoolInput(true);
    setSelectedSchool('');
  };

  const handleSelectAllForAssignment = () => {
    const unassignedRecipients = validRecipients.filter(
      r => !recipientAssignments[r.row_number]
    );
    if (selectedForAssignment.length === unassignedRecipients.length) {
      setSelectedForAssignment([]);
    } else {
      setSelectedForAssignment(unassignedRecipients.map(r => r.row_number));
    }
  };

  const handleRecipientToggle = (rowNumber) => {
    setSelectedForAssignment(prev => {
      if (prev.includes(rowNumber)) {
        return prev.filter(r => r !== rowNumber);
      } else {
        return [...prev, rowNumber];
      }
    });
  };

  // Assign selected gift/template/message to selected recipients
  const handleAssignToSelected = () => {
    if (!selectedGift || !selectedTemplate || selectedForAssignment.length === 0) {
      setError('Please select a Thnks, message template, and at least one recipient');
      return;
    }

    if (needsSchoolSelection && !hasValidSchool) {
      setError('Please select a school for this Thnks');
      return;
    }

    if (customMessage.length > MESSAGE_CHAR_LIMIT) {
      setError(`Message exceeds the ${MESSAGE_CHAR_LIMIT} character limit. Please shorten your message to fit on one page.`);
      return;
    }

    const schoolName = selectedSchool || customSchool || null;

    const newAssignments = { ...recipientAssignments };
    selectedForAssignment.forEach(rowNumber => {
      newAssignments[rowNumber] = {
        giftId: selectedGift,
        templateId: selectedTemplate,
        message: customMessage,
        school: schoolName
      };
    });

    setRecipientAssignments(newAssignments);
    setSelectedForAssignment([]);
    setError(null);

    // Clear school selection after assigning (for next assignment)
    setSelectedSchool('');
    setCustomSchool('');
    setSchoolSearch('');
    setShowCustomSchoolInput(false);
  };

  // Remove assignment from a recipient
  const handleRemoveAssignment = (rowNumber) => {
    const newAssignments = { ...recipientAssignments };
    delete newAssignments[rowNumber];
    setRecipientAssignments(newAssignments);
  };

  // Edit assignment for a recipient
  const handleEditAssignment = (rowNumber) => {
    const assignment = recipientAssignments[rowNumber];
    if (assignment) {
      setSelectedGift(assignment.giftId);
      setSelectedTemplate(assignment.templateId);
      setCustomMessage(assignment.message);
      // Restore school selection if applicable
      if (assignment.school) {
        if (schools.includes(assignment.school)) {
          setSelectedSchool(assignment.school);
          setCustomSchool('');
          setShowCustomSchoolInput(false);
        } else {
          setCustomSchool(assignment.school);
          setSelectedSchool('');
          setShowCustomSchoolInput(true);
        }
      }
      // Remove from assignments and add to selection for re-assignment
      handleRemoveAssignment(rowNumber);
      setSelectedForAssignment([rowNumber]);
    }
  };

  const handleMouseEnter = (recipient, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPreviewPosition({
      x: rect.right + 10,
      y: rect.top
    });

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRecipient(recipient);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Delay hiding to allow mouse to move to preview
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRecipient(null);
    }, 150);
  };

  const handlePreviewMouseEnter = () => {
    // Cancel the hide timeout when entering preview
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handlePreviewMouseLeave = () => {
    // Hide preview when leaving the preview tooltip
    setHoveredRecipient(null);
  };

  const calculateTotalCost = () => {
    let total = 0;
    Object.values(recipientAssignments).forEach(assignment => {
      const gift = gifts.find(g => g.id === assignment.giftId);
      if (gift) {
        total += gift.cost;
      }
    });
    return total.toFixed(2);
  };

  const getAssignedCount = () => {
    return Object.keys(recipientAssignments).length;
  };

  const getUnassignedCount = () => {
    return validRecipients.length - getAssignedCount();
  };

  const isMessageTooLong = () => {
    return customMessage.length > MESSAGE_CHAR_LIMIT;
  };

  const getCharactersRemaining = () => {
    return MESSAGE_CHAR_LIMIT - customMessage.length;
  };

  // Replace template variables like {sender_name}, {college} with actual values
  const replaceMessageVariables = (message, recipient, assignment, senderEmail) => {
    if (!message) return message;

    let result = message;

    // Get sender info from cache based on email
    const senderInfo = senderEmail ? senderInfoCache[senderEmail] : null;
    const senderFullName = senderInfo?.full_name || '[Sender Name]';
    const senderFirstName = senderInfo?.first_name || '[First Name]';
    const senderLastName = senderInfo?.last_name || '[Last Name]';

    // Replace {sender_name} with sender info from database lookup
    result = result.replace(/\{sender_name\}/gi, senderFullName);
    result = result.replace(/\{sender_first_name\}/gi, senderFirstName);
    result = result.replace(/\{sender_last_name\}/gi, senderLastName);

    // Replace {college} with selected school, removing "The " prefix if present
    let school = assignment?.school || selectedSchool || customSchool || '[School Name]';
    if (school.toLowerCase().startsWith('the ')) {
      school = school.substring(4);
    }
    result = result.replace(/\{college\}/gi, school);

    // Replace recipient variables
    if (recipient) {
      result = result.replace(/\{first_name\}/gi, recipient.first_name || '');
      result = result.replace(/\{last_name\}/gi, recipient.last_name || '');
    }

    return result;
  };

  const handleSubmit = async () => {
    if (getAssignedCount() === 0) {
      setError('Please assign at least one recipient before placing order');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Group recipients by their gift/template/message/school combination
      const selectionGroups = {};
      Object.entries(recipientAssignments).forEach(([rowNumber, assignment]) => {
        const key = `${assignment.giftId}-${assignment.templateId}-${assignment.message}-${assignment.school || ''}`;
        if (!selectionGroups[key]) {
          selectionGroups[key] = {
            thnks_id: assignment.giftId,
            message_template: assignment.templateId,
            message: assignment.message,
            school: assignment.school || null,
            row_numbers: []
          };
        }
        selectionGroups[key].row_numbers.push(parseInt(rowNumber));
      });

      const selections = Object.values(selectionGroups);

      // Apply selection to job and submit gift requests
      const applyResponse = await apiService.applyThnksSelection(jobId, selections);
      const giftRequestResult = applyResponse.data?.gift_request_result;

      // Check for budget-related errors from the Thnks API
      if (giftRequestResult?.has_budget_error) {
        // Find the specific error message from the results
        const budgetError = giftRequestResult.results?.find(r => r.error_code === 'individual_budget_reached');
        const errorMessage = budgetError?.error_message ||
          "You've hit your spend target for the month. Please contact your team manager or reach out to support@thnks.com to increase your target.";

        setError(errorMessage);
        setIsSubmitting(false);
        return; // Don't post to sheets or navigate away
      }

      // Check if all gift requests failed (non-budget errors)
      if (giftRequestResult?.failure_count > 0 && giftRequestResult?.success_count === 0) {
        const firstError = giftRequestResult.results?.find(r => !r.success);
        setError(firstError?.error_message || 'Failed to submit gift request. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Only post to Google Sheets if gift request was successful
      try {
        const sheetsResponse = await apiService.postToGoogleSheets(jobId);
        setValidationData(prev => ({
          ...prev,
          sheetsResult: sheetsResponse.data,
          thnksSelection: {
            assignments: recipientAssignments,
            totalRecipients: getAssignedCount(),
            totalCost: calculateTotalCost(),
            gifts: gifts,
            templates: templates,
            recipients: validRecipients
          }
        }));
      } catch (sheetsError) {
        console.error('Error posting to sheets:', sheetsError);
        setValidationData(prev => ({
          ...prev,
          sheetsError: sheetsError.response?.data?.detail || 'Failed to post to Google Sheets',
          thnksSelection: {
            assignments: recipientAssignments,
            totalRecipients: getAssignedCount(),
            totalCost: calculateTotalCost(),
            gifts: gifts,
            templates: templates,
            recipients: validRecipients
          }
        }));
      }

      navigate('/results');
    } catch (err) {
      console.error('Error applying selection:', err);
      setError(err.response?.data?.detail || 'Failed to apply Thnks selection');
      setIsSubmitting(false);
    }
  };

  // Get a sample recipient for the live preview
  const getSampleRecipient = () => {
    // Use first selected recipient, or first unassigned, or first valid recipient
    if (selectedForAssignment.length > 0) {
      return validRecipients.find(r => r.row_number === selectedForAssignment[0]);
    }
    const unassigned = validRecipients.filter(r => !recipientAssignments[r.row_number]);
    if (unassigned.length > 0) {
      return unassigned[0];
    }
    return validRecipients[0];
  };

  const renderLivePreview = () => {
    const sampleRecipient = getSampleRecipient();
    if (!sampleRecipient) return null;

    const gift = gifts.find(g => g.id === selectedGift);
    const hasContent = selectedTemplate || customMessage;

    return (
      <div className="live-preview-card">
        <div className="live-preview-header">
          <h3>Document Preview</h3>
          {hasContent && <span className="preview-badge">Live</span>}
        </div>
        <div className="live-preview-document">
          <div className="document-body">
            {/* Recipient Address Block */}
            <div className="document-address-block">
              <div className="address-name">
                {sampleRecipient.first_name} {sampleRecipient.last_name}
              </div>
              <div className="address-line">{sampleRecipient.address_1}</div>
              <div className="address-line">
                {sampleRecipient.city}, {sampleRecipient.state} {sampleRecipient.zip_code}
              </div>
            </div>

            {/* Greeting */}
            <div className="document-greeting">
              Dear {sampleRecipient.first_name},
            </div>

            {/* Message Body */}
            <div className="document-message">
              {customMessage ? (
                replaceMessageVariables(customMessage, sampleRecipient, null, sampleRecipient.email)
              ) : (
                <span className="placeholder-text">
                  Select a message template to preview your letter...
                </span>
              )}
            </div>

            {/* Closing */}
            <div className="document-closing">
              <div className="closing-line">Sincerely,</div>
              <div className="signature-name">
                {senderInfoCache[sampleRecipient.email]?.full_name || '[Sender Name]'}
              </div>
              <div className="signature-email">{sampleRecipient.email}</div>
            </div>
          </div>
        </div>

        {/* Gift Info Footer */}
        {gift && (
          <div className="live-preview-gift">
            <span className="gift-label">Selected Thnks:</span>
            <span className="gift-value">{gift.name} (${gift.cost.toFixed(2)})</span>
          </div>
        )}
      </div>
    );
  };

  const renderHoverPreview = () => {
    if (!hoveredRecipient) return null;

    const assignment = recipientAssignments[hoveredRecipient.row_number];
    if (!assignment) return null;

    const gift = gifts.find(g => g.id === assignment.giftId);
    const template = templates.find(t => t.id === assignment.templateId);

    return (
      <div
        className="preview-tooltip"
        style={{
          position: 'fixed',
          left: Math.min(previewPosition.x, window.innerWidth - 350),
          top: Math.min(previewPosition.y, window.innerHeight - 400),
        }}
        onMouseEnter={handlePreviewMouseEnter}
        onMouseLeave={handlePreviewMouseLeave}
      >
        <div className="preview-header">
          <span className="preview-title">Mail Merge Preview</span>
        </div>
        <div className="preview-content">
          <div className="preview-letter">
            <div className="preview-recipient">
              <strong>{hoveredRecipient.first_name} {hoveredRecipient.last_name}</strong>
              <br />
              {hoveredRecipient.address_1}
              <br />
              {hoveredRecipient.city}, {hoveredRecipient.state} {hoveredRecipient.zip_code}
            </div>
            <div className="preview-message">
              {replaceMessageVariables(assignment.message, hoveredRecipient, assignment, hoveredRecipient.email)}
            </div>
            <div className="preview-signature">
              — {senderInfoCache[hoveredRecipient.email]?.full_name || '[Sender Name]'}
            </div>
          </div>
          <div className="preview-details">
            <div className="preview-detail-item">
              <span className="preview-label">Thnks:</span>
              <span className="preview-value">{gift?.name}</span>
            </div>
            <div className="preview-detail-item">
              <span className="preview-label">Template:</span>
              <span className="preview-value">{template?.name}</span>
            </div>
            <div className="preview-detail-item">
              <span className="preview-label">Cost:</span>
              <span className="preview-value">${gift?.cost?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="thnks-selection-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading available Thnks...</p>
        </div>
      </div>
    );
  }

  const unassignedRecipients = validRecipients.filter(r => !recipientAssignments[r.row_number]);
  const assignedRecipients = validRecipients.filter(r => recipientAssignments[r.row_number]);

  return (
    <div className="thnks-selection-page">
      {/* Progress Indicator - Now at the very top */}
      <div className="progress-indicator top-progress">
        <div className="progress-stats">
          <span className="assigned-count">{getAssignedCount()} assigned</span>
          <span className="progress-divider">|</span>
          <span className="unassigned-count">{getUnassignedCount()} remaining</span>
          <span className="progress-divider">|</span>
          <span className="total-recipients">{validRecipients.length} total</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(getAssignedCount() / validRecipients.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="selection-header">
        <h1>Select Your Thnks</h1>
        <p>Assign Thnks and personalized messages to your validated recipients</p>
        <p>NOTE: All orders received by Tuesday at 6 PM CST will be included in the current week's shipment.{' '}
          <span className="shipping-link" onClick={() => setShowShippingModal(true)}>
            See Shipping Details
          </span>
        </p>
        <p>Disclaimer: For the College Flex Bottle, most colleges that participate in the NCAA are available but not all. Notre Dame is not available due to licensing agreements. Please choose another direct send item.</p>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="selection-layout three-column">
        {/* Left Panel: Gift & Message Selection */}
        <div className="selection-controls">
          {/* Gift Selection */}
          <div className="selection-card compact">
            <h2>1. Choose a Thnks</h2>
            <div className="gift-list">
              {gifts.map(gift => (
                <div
                  key={gift.id}
                  className={`gift-item ${selectedGift === gift.id ? 'selected' : ''}`}
                  onClick={() => setSelectedGift(gift.id)}
                >
                  <div className="gift-info">
                    <span className="gift-name">{gift.name}</span>
                    <span className="gift-cost">${gift.cost.toFixed(2)}</span>
                  </div>
                  {selectedGift === gift.id && <span className="check-icon">&#10003;</span>}
                </div>
              ))}
            </div>
          </div>

          {/* School Selection - shown when selected gift requires school selection (from API) */}
          {needsSchoolSelection && (
            <div className="selection-card compact school-selection-card">
              <h2>Select School</h2>

              {/* Selected school display */}
              {(selectedSchool || customSchool) && (
                <div className="selected-school">
                  <span className="school-badge">
                    {selectedSchool || customSchool}
                    <button
                      className="clear-school-btn"
                      onClick={() => {
                        setSelectedSchool('');
                        setCustomSchool('');
                        setShowCustomSchoolInput(false);
                      }}
                    >
                      &#10005;
                    </button>
                  </span>
                </div>
              )}

              {/* Search input */}
              {!selectedSchool && !showCustomSchoolInput && (
                <>
                  <div className="school-search">
                    <input
                      type="text"
                      placeholder="Search schools..."
                      value={schoolSearch}
                      onChange={(e) => setSchoolSearch(e.target.value)}
                      className="school-search-input"
                    />
                  </div>

                  {/* School list */}
                  <div className="school-list">
                    {filteredSchools.length > 0 ? (
                      filteredSchools.slice(0, 50).map((school, idx) => (
                        <div
                          key={idx}
                          className="school-item"
                          onClick={() => handleSchoolSelect(school)}
                        >
                          {school}
                        </div>
                      ))
                    ) : schoolSearch.trim() ? (
                      <div className="no-schools-found">
                        <p>Don't see the school you're looking for?</p>
                        <button
                          className="btn-secondary custom-school-btn"
                          onClick={handleCustomSchoolToggle}
                        >
                          Enter custom school name
                        </button>
                      </div>
                    ) : null}
                    {filteredSchools.length > 50 && (
                      <div className="school-list-hint">
                        Showing first 50 results. Type to search...
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Custom school input */}
              {showCustomSchoolInput && !selectedSchool && (
                <div className="custom-school-input">
                  <label>Enter school name:</label>
                  <input
                    type="text"
                    placeholder="Enter school name..."
                    value={customSchool}
                    onChange={(e) => setCustomSchool(e.target.value)}
                    className="custom-school-field"
                  />
                  <button
                    className="btn-secondary back-to-search-btn"
                    onClick={() => {
                      setShowCustomSchoolInput(false);
                      setCustomSchool('');
                    }}
                  >
                    Back to search
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Message Template Selection */}
          <div className="selection-card compact">
            <h2>2. Choose a Message</h2>
            {!selectedGift ? (
              <div className="template-placeholder">
                <p>Select a Thnks above to see available message templates</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="template-placeholder">
                <p>Loading templates...</p>
              </div>
            ) : (
              <div className="template-list">
                {templates.map(template => (
                  <div
                    key={template.id}
                    className={`template-item ${selectedTemplate === template.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTemplate(template.id)}
                  >
                    <span className="template-name">{template.name}</span>
                    {selectedTemplate === template.id && <span className="check-icon">&#10003;</span>}
                  </div>
                ))}
              </div>
            )}

            {selectedTemplate && (
              <div className="message-editor">
                <label>Edit Message:</label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={5}
                  className={isMessageTooLong() ? 'error' : ''}
                />
                <div className={`char-count ${isMessageTooLong() ? 'error' : ''}`}>
                  {customMessage.length} / {MESSAGE_CHAR_LIMIT} characters
                  {isMessageTooLong() && (
                    <span className="char-warning"> (exceeds page limit)</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Middle Panel: Recipients to Assign + Assigned Recipients */}
        <div className="recipients-panel">
          <div className="selection-card recipients-card">
            <div className="card-header">
              <h2>3. Select Recipients</h2>
              {unassignedRecipients.length > 0 && (
                <button
                  className="btn-secondary select-all-btn"
                  onClick={handleSelectAllForAssignment}
                >
                  {selectedForAssignment.length === unassignedRecipients.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {unassignedRecipients.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">&#10003;</span>
                <p>All recipients assigned!</p>
              </div>
            ) : (
              <div className="recipient-list">
                {unassignedRecipients.map(recipient => (
                  <div
                    key={recipient.row_number}
                    className={`recipient-item unassigned ${selectedForAssignment.includes(recipient.row_number) ? 'selected' : ''}`}
                    onClick={() => handleRecipientToggle(recipient.row_number)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedForAssignment.includes(recipient.row_number)}
                      onChange={() => handleRecipientToggle(recipient.row_number)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="recipient-info">
                      <span className="recipient-name">
                        {recipient.first_name} {recipient.last_name}
                      </span>
                      <span className="recipient-location">
                        {recipient.city}, {recipient.state}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Assign Button */}
            <button
              className="btn-primary assign-btn"
              onClick={handleAssignToSelected}
              disabled={!selectedGift || !selectedTemplate || selectedForAssignment.length === 0 || isMessageTooLong() || (needsSchoolSelection && !hasValidSchool)}
            >
              {isMessageTooLong()
                ? 'Message too long'
                : (needsSchoolSelection && !hasValidSchool)
                  ? 'Select a school'
                  : `Assign to ${selectedForAssignment.length} Recipient${selectedForAssignment.length !== 1 ? 's' : ''}`}
            </button>
          </div>

          {/* Assigned Recipients */}
          {assignedRecipients.length > 0 && (
            <div className="selection-card assigned-card">
              <div className="card-header">
                <h2>Assigned ({assignedRecipients.length})</h2>
              </div>
              <div className="recipient-list assigned-list">
                {assignedRecipients.map(recipient => {
                  const assignment = recipientAssignments[recipient.row_number];
                  const gift = gifts.find(g => g.id === assignment?.giftId);
                  const template = templates.find(t => t.id === assignment?.templateId);

                  return (
                    <div
                      key={recipient.row_number}
                      className="recipient-item assigned"
                      onMouseEnter={(e) => handleMouseEnter(recipient, e)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="assigned-indicator">
                        <span className="assigned-check">&#10003;</span>
                      </div>
                      <div className="recipient-info">
                        <span className="recipient-name">
                          {recipient.first_name} {recipient.last_name}
                        </span>
                        <span className="recipient-assignment">
                          {gift?.name}{assignment?.school ? ` (${assignment.school})` : ''} • {template?.name}
                        </span>
                      </div>
                      <div className="recipient-actions">
                        <button
                          className="btn-icon edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAssignment(recipient.row_number);
                          }}
                          title="Edit"
                        >
                          &#9998;
                        </button>
                        <button
                          className="btn-icon remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAssignment(recipient.row_number);
                          }}
                          title="Remove"
                        >
                          &#10005;
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel: Live Preview */}
        <div className="preview-panel">
          {renderLivePreview()}
        </div>
      </div>


      {/* Hover Preview */}
      {renderHoverPreview()}

      {/* Cost Summary & Submit */}
      <div className="summary-section">
        <div className="cost-summary">
          <div className="cost-breakdown">
            <span>
              {getAssignedCount()} recipient{getAssignedCount() !== 1 ? 's' : ''} assigned
            </span>
            <span className="total-cost">
              Total: ${calculateTotalCost()}
            </span>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="btn-secondary"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || getAssignedCount() === 0}
          >
            {isSubmitting ? 'Processing...' : 'Place Order'}
          </button>
        </div>
      </div>

      {/* Shipping Details Modal */}
      {showShippingModal && (
        <div className="modal-overlay" onClick={() => setShowShippingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Shipping Details</h2>
              <button className="modal-close" onClick={() => setShowShippingModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="shipping-item">
                <strong>UKG Brownies:</strong> Yummy Mummy Bakery will ship out on the following Tuesday and arrive in 1-2 business days via FedEx 2-day shipping.
              </div>
              <div className="shipping-item">
                <strong>College Flex Bottle:</strong> Ice Shaker will ship orders on Friday and arrive within 3-5 business days
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThnksSelectionPage;
